import React, { useState, useEffect } from 'react';
import api from '../services/api';
import PortalLayout, { card, StateMsg, SearchBar, matchZoek, datumNL } from '../components/PortalLayout';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { FileOpener } from '@capacitor-community/file-opener';

const STATUS_KLEUR = {
  afgerond: { bg: '#ecfdf5', fg: '#047857' },
  nieuw: { bg: '#eff6ff', fg: '#1d4ed8' },
  bezig: { bg: '#fffbeb', fg: '#b45309' },
  in_uitvoering: { bg: '#fffbeb', fg: '#b45309' },
  toegewezen: { bg: '#fffbeb', fg: '#b45309' },
  restwerk: { bg: '#fff7ed', fg: '#c2410c' },
};
const AFGEROND = ['afgerond'];

export default function Werkorders() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [zoek, setZoek] = useState('');
  const [tab, setTab] = useState('open');
  const [openId, setOpenId] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(null);
  useEffect(() => {
    api.get('/klantportaal/werkorders')
      .then((r) => setItems(r.data.werkorders || []))
      .catch(() => setError('Kon werkorders niet laden'));
  }, []);
  const alles = items || [];
  const inTab = alles.filter((w) => tab === 'afgerond' ? AFGEROND.includes(w.status) : !AFGEROND.includes(w.status));
  const zichtbaar = inTab.filter((w) => matchZoek(zoek, w.titel, w.ordernummer, w.status, w.locatie_adres));
  const nOpen = alles.filter((w) => !AFGEROND.includes(w.status)).length;
  const nAf = alles.length - nOpen;
  const downloadPdf = async (w) => {
    setPdfBusy(w.id);
    try {
      const r = await api.get('/klantportaal/werkorders/' + w.id + '/pdf', { responseType: 'blob' });
      const naam = 'werkbon-' + (w.ordernummer || w.id) + '.pdf';
      if (Capacitor.isNativePlatform()) {
        const base64 = await new Promise((res, rej) => {
          const fr = new FileReader();
          fr.onload = () => res(String(fr.result).split(',')[1]);
          fr.onerror = rej;
          fr.readAsDataURL(r.data);
        });
        const file = await Filesystem.writeFile({ path: naam, data: base64, directory: Directory.Cache });
        try {
          await FileOpener.open({ filePath: file.uri, contentType: 'application/pdf', openWithDefault: false });
        } catch (openErr) {
          await Share.share({ title: naam, url: file.uri });
        }
      } else {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = naam;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { if (!/canceled|cancelled/i.test(String(e?.message))) alert('Kon werkbon niet laden'); }
    setPdfBusy(null);
  };
  const tabStyle = (actief) => ({
    flex: 1, textAlign: 'center', padding: '10px 0', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer',
    background: actief ? '#00D4AA' : '#fff', color: actief ? '#fff' : '#64748b', border: '1px solid ' + (actief ? '#00D4AA' : '#e2e8f0'),
  });
  return (
    <PortalLayout titel="Werkorders">
      {items && items.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <div style={tabStyle(tab === 'open')} onClick={() => setTab('open')}>Lopend ({nOpen})</div>
            <div style={tabStyle(tab === 'afgerond')} onClick={() => setTab('afgerond')}>Afgerond ({nAf})</div>
          </div>
          <SearchBar value={zoek} onChange={setZoek} placeholder="Zoek op titel, order of status..." />
        </>
      )}
      <StateMsg loading={!items && !error} error={error} leeg={items && items.length === 0} leegTekst="Nog geen werkorders." />
      {items && items.length > 0 && (
        <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 10 }}>{zichtbaar.length} van {inTab.length}</div>
      )}
      {zichtbaar.map((w) => {
        const k = STATUS_KLEUR[w.status] || { bg: '#f1f5f9', fg: '#475569' };
        const open = openId === w.id;
        return (
          <div key={w.id} style={{ ...card, cursor: 'pointer' }} onClick={() => setOpenId(open ? null : w.id)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ fontWeight: 600, color: '#1e293b' }}>{w.titel || 'Werkorder'}</div>
              <span style={{ background: k.bg, color: k.fg, fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap', textTransform: 'capitalize' }}>{w.status}</span>
            </div>
            <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
              {[w.ordernummer ? 'order ' + w.ordernummer : null, w.locatie_adres].filter(Boolean).join(' \u00b7 ')}
            </div>
            <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 6 }}>
              {w.afgerond_op ? 'Afgerond ' + datumNL(w.afgerond_op) : 'Aangemaakt ' + datumNL(w.created_at)}
            </div>
            {open && (
              <div style={{ marginTop: 12, borderTop: '1px solid #f1f5f9', paddingTop: 12 }} onClick={(e) => e.stopPropagation()}>
                {w.beschrijving && <div style={{ fontSize: 14, color: '#334155', marginBottom: 10, whiteSpace: 'pre-wrap' }}>{w.beschrijving}</div>}
                <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 10 }}>
                  {w.afdeling_naam ? 'Afdeling: ' + w.afdeling_naam : ''}
                </div>
                {AFGEROND.includes(w.status) && (
                  <button onClick={() => downloadPdf(w)} disabled={pdfBusy === w.id}
                    style={{ background: '#00D4AA', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer', width: '100%' }}>
                    {pdfBusy === w.id ? 'Laden...' : '\ud83d\udcc4 Werkbon (PDF)'}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </PortalLayout>
  );
}
